import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

import arrowDownDark from "@/assets/images/Arrow_drop_down.svg";
import arrowDownWhite from "@/assets/images/Arrow_drop_down_white_dark_mode.svg";

const getPortalTarget = () =>
  typeof document !== "undefined"
    ? document.querySelector(".keobongvip-root") || document.body
    : null;

const BookmakerSelectV5 = ({
  value,
  onChange,
  options,
  placeholder = "",
  className = "",
  triggerClassName = "",
  scopeClassName = "",
  variant = "filled", // "filled" | "outline"
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, minWidth: 0 });
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const updatePosition = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: rect.width,
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    requestAnimationFrame(updatePosition);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const wrapper = ref.current;
      const dropdown = dropdownRef.current;
      if (
        wrapper &&
        !wrapper.contains(e.target) &&
        dropdown &&
        !dropdown.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  const dropdown = open && (
    <div
      ref={dropdownRef}
      className={`kbv-bookmaker-select-list-portal ${scopeClassName}`}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        minWidth: position.minWidth,
        zIndex: 99999,
      }}
    >
      <ul className="kbv-bookmaker-select-list max-h-[200px] overflow-y-auto rounded border border-[var(--theme-border)] bg-[var(--theme-foreground)] shadow-lg list-none m-0 p-0">
        {options.map((item) => (
          <li
            key={item.value}
            className={`kbv-bookmaker-select-item m-0 px-[10px] py-[7px] text-[13px] cursor-pointer whitespace-nowrap transition-colors ${
              String(item.value) === String(value)
                ? "kbv-bookmaker-select-item--active"
                : ""
            }`}
            onClick={() => handleSelect(item.value)}
          >
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );

  const portalTarget = getPortalTarget();

  return (
    <>
      <div
        className={`kbv-bookmaker-select-wrapper relative inline-block flex-shrink-0 ${scopeClassName} ${className}`}
        ref={ref}
      >
        <div
          className={`kbv-bookmaker-select-trigger flex items-center gap-1.5 w-full min-w-0 text-sm rounded cursor-pointer select-none ${
            variant === "outline" ? "kbv-bookmaker-select-trigger--outline" : ""
          } ${triggerClassName}`}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="flex-1 min-w-0 truncate">
            {selected ? selected.label : placeholder}
          </span>
          <img
            src={variant === "outline" ? arrowDownDark : arrowDownWhite}
            alt=""
            className={`kbv-bookmaker-select-arrow w-5 h-5 shrink-0 ${
              open ? "kbv-bookmaker-select-arrow--open" : ""
            }`}
          />
        </div>
      </div>
      {portalTarget && createPortal(dropdown, portalTarget)}
    </>
  );
};

export default BookmakerSelectV5;
